import { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, Legend } from 'recharts';
import html2canvas from 'html2canvas';
import { useTheme } from '@iq/react-components';
import { downloadBlob, formatDateString } from '../../../../utils';
import ListItem from '../../../ListItem';
import './ExportEventStats.scss';
import { USER_EVENTS } from '../../../../constants';

const canvasToSvg = async (element) => {
  try {
    const canvas = await html2canvas(element);

    // Create SVG element
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('width', canvas.width);
    svg.setAttribute('height', canvas.height);

    // Create SVG image element with Canvas data
    const svgImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    svgImage.setAttribute('x', '0');
    svgImage.setAttribute('y', '0');
    svgImage.setAttribute('width', canvas.width);
    svgImage.setAttribute('height', canvas.height);
    svgImage.setAttributeNS(
      'http://www.w3.org/1999/xlink',
      'xlink:href',
      canvas.toDataURL('image/png')
    );

    // Append image element to SVG
    svg.append(svgImage);

    return new XMLSerializer().serializeToString(svg);
  } catch (error) {
    console.error('Error converting canvas to SVG:', error);
    return null;
  }
};

const listItems = (events) =>
  events.map((userEvent) => (
    <ListItem
      key={userEvent.id}
      entity={`exportStats (${userEvent.name})`}
      item={userEvent}
      columns={[
        userEvent.name,
        userEvent.value || 0,
        userEvent.pending || 0,
        userEvent.closed || 0,
        userEvent.open || 0,
        userEvent.overdue || 0,
        userEvent.todo || 0,
      ]}
      columnWidths={{ type: 'rem', widths: [11, 7, 7, 7, 7, 7, 7] }}
      withActions={false}
    ></ListItem>
  ));

const PieChartExport = ({
  site: siteId,
  eventsStats,
  onClose,
  setEventsStatsRetCount,
  dateDisplay,
  startDate,
  endDate,
  allUserEventTypes,
}) => {
  const chartReference = useRef(null);
  const [animationEndAck, setAnimationEndAck] = useState(0);

  const theme = useTheme();
  const transformEvents = (events, keyName) =>
    events.reduce((acc, eventStat) => {
      const eventKey = eventStat[keyName];

      const userEvent =
        keyName === 'state'
          ? USER_EVENTS.find((ue) => ue.id === eventKey)
          : allUserEventTypes.find((uet) => uet.id === eventKey);

      const key = keyName === 'state' ? userEvent.type : userEvent.name;

      const supportingKeyName = keyName === 'state' ? 'type' : 'state';

      const existingObject = acc.find((item) => item.name === key);

      const count = Number(eventStat.count);

      if (eventStat.count) {
        if (existingObject) {
          existingObject[eventStat[supportingKeyName]] = count;
          existingObject.value += count;
        } else {
          const color = keyName === 'state' ? USER_EVENTS.find((ue) => ue.type === key).color : '';

          acc.push({
            name: key,
            [eventStat[supportingKeyName]]: count,
            value: count,
            fill: color,
          });
        }
      }

      return acc;
    }, []);

  const plannedEvents = [];
  const reactiveEvents = [];
  for (const event of eventsStats) {
    if (event.planned) {
      plannedEvents.push(event);
    } else {
      reactiveEvents.push(event);
    }
  }

  const stateGroupedPlannedEvents = transformEvents(plannedEvents, 'state');
  const stateGroupedReactiveEvents = transformEvents(reactiveEvents, 'state');
  const typeGroupedPlannedEvents = transformEvents(plannedEvents, 'type');
  const typeGroupedReactiveEvents = transformEvents(reactiveEvents, 'type');

  const exportToSVG = useCallback(async () => {
    const chart = chartReference.current;
    const hideElementStyle = {
      display: 'flex',
      position: 'absolute',
      left: '-800rem',
    };

    Object.assign(chart.style, hideElementStyle);
    const svgXml = await canvasToSvg(chart);
    const fromDate = formatDateString(startDate);
    const toDate = formatDateString(endDate);
    const fileName = `${siteId}-Exported Statistics-${fromDate}_${toDate}.svg`;
    downloadBlob(svgXml, fileName, 'image/svg+xml;charset=utf-8');
    chart.display = 'none';
    setEventsStatsRetCount(10_000);
    onClose();
  }, [siteId, startDate, endDate, setEventsStatsRetCount, onClose]);

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    value,
    percent,
  }) => {
    const percentwithDec = percent * 100;
    const displayPercent =
      percentwithDec < 1 ? percentwithDec.toFixed(1) : percentwithDec.toFixed(0);
    const offset = displayPercent < 5 ? 125 : 20;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5 + offset;
    const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
    const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

    return (
      <>
        {displayPercent < 5 && (
          <line
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke={'#b3b3b3'}
            fill={`${theme === 'dark' ? '#e6e6e6' : '#1a1a1a'}`}
            strokeWidth={1}
          />
        )}
        <text
          x={x}
          y={y}
          fill={`${theme === 'dark' ? '#e6e6e6' : '#1a1a1a'}`}
          textAnchor="start"
          dominantBaseline="middle"
          fontSize={displayPercent < 5 ? '0.7em' : '1em'}
        >
          {`${value} (${displayPercent}%)`}
        </text>
      </>
    );
  };

  const onEnd = () => {
    setAnimationEndAck(animationEndAck + 1);
  };

  useEffect(() => {
    if (animationEndAck === 2) {
      exportToSVG();
    }
  }, [animationEndAck, exportToSVG]);

  const headers = ['Type', 'Total', 'Pending', 'Closed', 'Open', 'Overdue', 'To-do'];

  return (
    <div className="export-event-stats">
      {/* Hidden div to hold the chart for export */}
      <div
        className="report"
        ref={chartReference}
        style={{ display: 'none' }}
      >
        <div className="chart">
          <div className="label">Status report (Planned tasks)</div>
          <h1>{dateDisplay}</h1>

          {stateGroupedPlannedEvents?.length > 0 ? (
            <>
              <PieChart
                width={600}
                height={600}
              >
                <Pie
                  data={stateGroupedPlannedEvents}
                  cx={300}
                  cy={300}
                  innerRadius={0}
                  outerRadius={180}
                  labelLine={false}
                  fill="#8884d8"
                  dataKey="value"
                  label={renderCustomizedLabel}
                  onAnimationEnd={onEnd}
                >
                  {stateGroupedPlannedEvents.map((entry) => (
                    <Cell
                      key={`cell-${entry.name}`}
                      fill={entry.fill}
                    />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
              <div className="list-container">
                <ListItem
                  isHeader
                  columns={headers}
                  columnWidths={{ type: 'rem', widths: [11, 7, 7, 7, 7, 7, 7] }}
                  withActions={false}
                />
                <div className="custom-thin-scrollbar">{listItems(typeGroupedPlannedEvents)}</div>
              </div>
            </>
          ) : (
            <div className="noEvents">There is no planned task in selected time range</div>
          )}
        </div>
        <div className="chart">
          <div className="label">Status report (Reactive tasks)</div>
          <h1>{dateDisplay}</h1>
          {stateGroupedReactiveEvents?.length > 0 ? (
            <>
              <PieChart
                width={600}
                height={600}
              >
                <Pie
                  data={stateGroupedReactiveEvents}
                  cx={300}
                  cy={300}
                  innerRadius={0}
                  outerRadius={180}
                  fill="#8884d8"
                  dataKey="value"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  onAnimationEnd={onEnd}
                >
                  {stateGroupedReactiveEvents.map((entry) => (
                    <Cell
                      key={`cell-${entry.name}`}
                      fill={entry.fill}
                    />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
              <div className="list-container">
                <ListItem
                  isHeader
                  columns={headers}
                  columnWidths={{ type: 'rem', widths: [11, 7, 7, 7, 7, 7, 7] }}
                  withActions={false}
                ></ListItem>
                {listItems(typeGroupedReactiveEvents)}
              </div>
            </>
          ) : (
            <div className="noEvents">There is no reactive task in selected time range</div>
          )}
        </div>
      </div>
    </div>
  );
};

PieChartExport.propTypes = {
  site: PropTypes.string.isRequired,
  eventsStats: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  setEventsStatsRetCount: PropTypes.func.isRequired,
  dateDisplay: PropTypes.string.isRequired,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  allUserEventTypes: PropTypes.array.isRequired,
};

export default PieChartExport;
